import { FLATTEN_TAGS, HEADING_TAGS } from './chat-html-contract';
import { ChatInlineContentNormalizer } from './chat-inline-content-normalizer';

export class ChatSemanticStructureNormalizer {
  constructor(private readonly inline: ChatInlineContentNormalizer) {}

  translate(sanitizedHtml: string): string | null {
    const source = (sanitizedHtml || '').trim();
    if (!source) {
      return null;
    }
    let parsed: Document;
    try {
      parsed = new DOMParser().parseFromString(`<div>${source}</div>`, 'text/html');
    } catch {
      return null;
    }
    const root = parsed.body?.firstElementChild;
    if (!root?.textContent?.trim()) {
      return null;
    }
    const card = parsed.createElement('div');
    card.className = 'ai-answer';
    this.appendChildren(root, card, parsed);
    return card.childNodes.length ? card.outerHTML : null;
  }

  private appendChildren(source: Element, target: Element, doc: Document): void {
    source.childNodes.forEach((node) => {
      const normalized = this.normalizeNode(node, doc);
      if (normalized) {
        target.appendChild(normalized);
      }
    });
  }

  private normalizeNode(node: Node, doc: Document): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      return text.trim() ? doc.createTextNode(text) : null;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const element = node as Element;
    if (HEADING_TAGS.has(element.tagName)) {
      return this.buildHeading(element, doc);
    }
    if (element.tagName === 'TABLE') {
      return this.buildTable(element, doc);
    }
    if (element.tagName === 'UL' || element.tagName === 'OL') {
      return this.buildList(element, doc);
    }
    if (element.tagName === 'LI') {
      return this.buildListItem(element, doc);
    }
    if (element.tagName === 'P') {
      return this.buildParagraph(element, doc);
    }
    return this.buildContainer(element, doc);
  }

  private buildHeading(element: Element, doc: Document): Node {
    const heading = doc.createElement(element.tagName.toLowerCase());
    heading.className = element.tagName === 'H1' || element.tagName === 'H2'
      ? 'ai-title'
      : 'ai-section-title';
    this.inline.copy(element, heading, doc);
    return heading;
  }

  private buildTable(element: Element, doc: Document): Node {
    const table = doc.createElement('table');
    table.className = 'ai-table';
    element.childNodes.forEach((child) => {
      const normalized = this.inline.cloneTableStructure(child, doc);
      if (normalized) {
        table.appendChild(normalized);
      }
    });
    return table;
  }

  private buildList(element: Element, doc: Document): Node {
    const items = Array.from(element.children).filter((child) => child.tagName === 'LI');
    const allKeyValues = items.length > 0
      && items.every((item) => this.inline.parseKeyValue(item.textContent ?? '') !== null);
    if (allKeyValues) {
      const group = doc.createElement('div');
      group.className = 'ai-kv';
      items.forEach((item) => group.appendChild(this.buildKeyValue(item.textContent ?? '', doc)));
      return group;
    }
    const list = doc.createElement(element.tagName.toLowerCase());
    element.childNodes.forEach((child) => {
      const normalized = this.normalizeNode(child, doc);
      if (normalized) {
        list.appendChild(normalized);
      }
    });
    return list;
  }

  private buildListItem(element: Element, doc: Document): Node {
    if (this.inline.parseKeyValue(element.textContent ?? '')) {
      return this.buildKeyValue(element.textContent ?? '', doc);
    }
    const item = doc.createElement('li');
    this.inline.copy(element, item, doc);
    return item;
  }

  private buildParagraph(element: Element, doc: Document): Node {
    if (this.inline.parseKeyValue(element.textContent ?? '')) {
      return this.buildKeyValue(element.textContent ?? '', doc);
    }
    const paragraph = doc.createElement('p');
    this.inline.copy(element, paragraph, doc);
    return paragraph;
  }

  private buildKeyValue(rawText: string, doc: Document): Element {
    const parsed = this.inline.parseKeyValue(rawText);
    const label = parsed?.label ?? '';
    const value = parsed?.value ?? rawText.trim();
    const item = doc.createElement('div');
    item.className = 'ai-kv-item';
    const labelElement = doc.createElement('span');
    labelElement.className = 'ai-kv-label';
    labelElement.textContent = label;
    const valueElement = doc.createElement('span');
    valueElement.className = 'ai-kv-value';
    valueElement.textContent = value;
    const status = this.inline.detectStatus(value);
    if (status) {
      valueElement.classList.add('ai-status', `ai-status--${status}`);
    }
    item.appendChild(labelElement);
    item.appendChild(valueElement);
    return item;
  }

  private buildContainer(element: Element, doc: Document): Node | null {
    const tag = element.tagName;
    if (FLATTEN_TAGS.has(tag)) {
      const section = doc.createElement('section');
      section.className = 'ai-section';
      this.appendChildren(element, section, doc);
      if (!section.childNodes.length) {
        return null;
      }
      const status = this.inline.explicitStatus(element);
      if (status) {
        section.classList.add('ai-status', `ai-status--${status}`);
      }
      return section;
    }
    if (tag === 'HR') {
      return doc.createElement('hr');
    }
    if (tag === 'BLOCKQUOTE') {
      const note = doc.createElement('p');
      note.className = 'ai-note';
      this.inline.copy(element, note, doc);
      return note;
    }
    const clone = doc.createElement(tag.toLowerCase());
    if (tag === 'A') {
      this.inline.copySafeAnchorAttributes(element, clone);
    }
    this.inline.copy(element, clone, doc);
    return clone.textContent?.trim() ? clone : null;
  }
}
